import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Plus, Trash, X } from "@phosphor-icons/react";
import { fmtDateTimeET } from "@/lib/etTime";
import { useBulletinUnread } from "@/context/BulletinContext";

/**
 * Bulletin Board — supervisor-authored notices with expiry.
 *
 * Any authenticated user can read; only supervisor / staff_chief /
 * commanding_officer / super_admin can post or delete (server-gated).
 * Opening this page marks every currently-active bulletin as read for
 * the viewer, which stops the sidebar tab from blinking.
 */
const DURATION_OPTIONS = [
  { value: "1_day", label: "1 Day" },
  { value: "1_week", label: "1 Week" },
  { value: "1_month", label: "1 Month" },
  { value: "1_year", label: "1 Year" },
  { value: "indefinite", label: "Indefinite" },
];

export default function BulletinBoard() {
  const [bulletins, setBulletins] = useState([]);
  const [canPost, setCanPost] = useState(false);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [duration, setDuration] = useState("1_week");
  const [posting, setPosting] = useState(false);
  const { refresh: refreshUnread } = useBulletinUnread();

  async function load() {
    try {
      const { data } = await api.get("/bulletins/");
      setBulletins(data.bulletins || []);
      setCanPost(!!data.can_post);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // On mount: fetch the board and mark everything read so the sidebar
    // stops blinking. We fire the read-all POST but don't block the
    // render on it.
    load();
    api.post("/bulletins/read-all")
      .catch(() => {})
      .finally(() => refreshUnread());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (posting) return;
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setPosting(true);
    try {
      await api.post("/bulletins/", { title, body, duration });
      toast.success("Bulletin posted");
      setTitle(""); setBody(""); setDuration("1_week");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  }

  async function remove(b) {
    if (!window.confirm(`Delete bulletin "${b.title}"? This is permanent.`)) return;
    try {
      await api.delete(`/bulletins/${b.id}`);
      toast.success("Bulletin removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <AppLayout title="Bulletin Board" subtitle="Command postings — read every time you log in">
      {canPost && (
        <form onSubmit={submit} className="surface-grid p-5 mb-5" data-testid="bulletin-post-form">
          <div className="flex items-center gap-2 mb-3">
            <Megaphone size={18} weight="bold" className="text-slate-700" />
            <h3 className="font-display text-base font-bold">Post a new bulletin</h3>
          </div>
          <div className="grid gap-3">
            <div>
              <Label className="tab-tag">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm" maxLength={200} data-testid="bulletin-title" />
            </div>
            <div>
              <Label className="tab-tag">Body</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="rounded-sm" data-testid="bulletin-body" />
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="tab-tag">Stay Up For</Label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="h-10 rounded-sm border border-slate-200 px-3 text-sm bg-white min-w-[10rem]"
                  data-testid="bulletin-duration"
                >
                  {DURATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <Button type="submit" disabled={posting} className="rounded-sm bg-slate-900 text-white" data-testid="bulletin-submit">
                <Plus size={14} weight="bold" className="mr-1" />{posting ? "Posting…" : "Post Bulletin"}
              </Button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : bulletins.length === 0 ? (
        <div className="surface-grid p-8 text-center text-slate-500" data-testid="bulletin-empty">
          <Megaphone size={32} weight="bold" className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No active bulletins right now.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="bulletin-list">
          {bulletins.map((b) => (
            <li key={b.id} className="surface-grid p-4" data-testid={`bulletin-item-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h4 className="font-display text-base font-bold text-slate-900">{b.title}</h4>
                  <div className="tab-tag text-slate-500 mt-0.5">
                    Posted by <span className="text-slate-700">{b.author_name || "—"}</span> · {fmtDateTimeET(b.posted_at)}
                    {b.expires_at ? (
                      <> · <Badge className="rounded-sm bg-amber-100 text-amber-800 text-[10px] tracking-widest ml-1">Expires {fmtDateTimeET(b.expires_at)}</Badge></>
                    ) : (
                      <> · <Badge className="rounded-sm bg-slate-200 text-slate-800 text-[10px] tracking-widest ml-1">Indefinite</Badge></>
                    )}
                  </div>
                </div>
                {canPost && (
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(b)} data-testid={`bulletin-del-${b.id}`}>
                    <Trash size={14} />
                  </Button>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{b.body}</p>
            </li>
          ))}
        </ul>
      )}
    </AppLayout>
  );
}
